import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "results", "tab", "blank", "CompanyBadge", "AssetBadge", "PasswordBadge", "ArticleBadge", "WebsiteBadge", "ProcessBadge", "numResults", "backButton", "empty"]
  static values = {
    structureName: String,
    structurePlural: { type: String, default: "" }
  }
  static minSearchChars = 3 // Number of characters to trigger search

  connect() {
    this.selectedIndex = -1
    this.bindKeyboardEvents()
    // Initialize search preferences
    const comprehensiveChip = this.element.querySelector('.chip--selectable[data-search-comprehensive-default-value]')
    this.searchByAll = comprehensiveChip?.dataset.searchByAll === "true"
    this.searchByExact = false
    this.onlyMuseum = false
    // Get initial company state from the active chip if it exists
    const companyChip = this.element.querySelector('.chip--selectable[data-company-id]')
    this.companyId = companyChip?.dataset.companyId
    this.onlyCompany = companyChip?.classList.contains('active') || false

    // Check if we're in a specific view mode (no tabs visible)
    const specificHeader = this.element.querySelector('.searcher__header--specific')
    this.specificViewMode = specificHeader !== null

    // Initialize view mode tracking
    this.currentViewMode = 'all' // 'all' or 'single'
    this.currentViewType = null

    // If we're in specific view mode, get the view type from the icon class
    if (this.specificViewMode) {
      const iconElement = this.element.querySelector('.searcher__icon')
      if (iconElement) {
        this.specificViewType = this.getTypeFromIconClass(iconElement.className)
      }
    }

    // Initialize debounce timer
    this.debounceTimer = null
    this.debounceDelay = 100 // milliseconds - reduced from 300ms for faster response

    // Set initial state
    this.toggleBlankState(true)

    // Make the search modal focusable
    this.element.setAttribute('tabindex', '0')
    this.element.style.outline = 'none' // Remove focus outline since it's a modal

    // Hide header search when modal opens
    const headerSearch = document.querySelector('.header__search')
    if (headerSearch) {
      // Add a small delay to ensure the animation plays
      requestAnimationFrame(() => {
        headerSearch.classList.add('search--hidden')
      })
    }

    // Focus the modal when it opens
    this.element.focus()

    // Setup focus trap
    this.setupFocusTrap()
  }

  setupFocusTrap() {
    // This will handle the focus trap functionality
    this.element.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        // Get all focusable elements within the modal
        const focusableElements = Array.from(this.element.querySelectorAll(
          'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);

        if (focusableElements.length === 0) return;

        // If no elements, just focus the input
        if (focusableElements.length === 1) {
          e.preventDefault();
          this.inputTarget.focus();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Handle tabbing forward from last element
        if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          this.inputTarget.focus();
        }

        // Handle tabbing backward from first element
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      }
    });
  }

  bindKeyboardEvents() {
    // Remove any existing event listener to prevent duplicates
    this.element.removeEventListener('keydown', this.handleKeydown)

    // Create the handler and store it so we can remove it later
    this.handleKeydown = (e) => {
      // Add company chip toggle shortcut (Option + G)
      if (e.altKey && (e.key.toLowerCase() === 'g' || e.key === "©")) {  // Handle both Mac and non-Mac
        e.preventDefault()
        e.stopPropagation()
        const companyChip = this.element.querySelector('.chip--selectable[data-company-id]')
        if (companyChip) {
          this.toggleSearchPreference({ preventDefault: () => {}, currentTarget: companyChip })
        }
      } else {
        // Handle keyboard navigation regardless of focus
        switch(e.key) {
          case 'ArrowDown':
            e.preventDefault()
            this.moveSelection(1)
            break
          case 'ArrowUp':
            e.preventDefault()
            this.moveSelection(-1)
            break
          case 'Enter':
            // Check if a chip is focused first
            const focusedElement = document.activeElement
            if (focusedElement && focusedElement.closest('.chip--selectable')) {
              e.preventDefault()
              this.toggleSearchPreference({ preventDefault: () => {}, currentTarget: focusedElement })
            } else if (this.selectedIndex >= 0) {
              // If we have a selected result and no chip is focused, navigate to it
              e.preventDefault()
              this.selectCurrent()
            } else if (focusedElement && (focusedElement.tagName === 'A' || focusedElement.tagName === 'BUTTON')) {
              // If no result is selected and no chip is focused, try to click the focused element
              e.preventDefault()
              focusedElement.click()
            }
            break
          case 'Tab':
            // Let the default tab behavior happen
            break
        }

        // Always listen for Escape
        if (e.key === 'Escape') {
          e.preventDefault()
          const frame = this.element.closest('turbo-frame')
          if (frame) frame.src = ''
        }
      }
    }

    // Add the event listener with passive option for better scroll performance
    this.element.addEventListener('keydown', this.handleKeydown, { passive: false })
  }

  moveSelection(direction) {
    const results = this.resultsTarget.querySelectorAll('.searcher__result')
    if (results.length > 0) {
      // Remove current selection
      results.forEach(result => result.classList.remove('searcher__result--selected'))

      // Update index
      this.selectedIndex += direction
      if (this.selectedIndex >= results.length) this.selectedIndex = 0
      if (this.selectedIndex < 0) this.selectedIndex = results.length - 1

      // Add selection to new item
      const selectedResult = results[this.selectedIndex]
      selectedResult.classList.add('searcher__result--selected')
      selectedResult.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrent() {
    const results = this.resultsTarget.querySelectorAll('.searcher__result')
    const selected = results[this.selectedIndex]
    if (selected) {
      const href = selected.getAttribute('href')
      if (href) window.location.href = href
    }
  }

  getCurrentType() {
    // If we're in specific view mode, return the specific type
    if (this.specificViewMode && this.specificViewType) {
      return this.specificViewType
    }

    // Otherwise use the active tab
    const activeTab = this.tabTargets.find(tab =>
      tab.classList.contains('searcher__tab--active')
    )
    return activeTab ? activeTab.dataset.type : 'Asset'
  }

  toggleBlankState(show) {
    if (this.hasBlankTarget) {
      this.blankTarget.style.display = show ? 'flex' : 'none'
      this.resultsTarget.style.display = show ? 'none' : 'block'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = show ? 'none' : 'block'
    }
  }

  toggleSearchPreference(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest('.chip--selectable')

    // Handle company chip differently since it's exclusive
    if (chip.dataset.companyId) {
      this.onlyCompany = !this.onlyCompany
      chip.classList.toggle('active')
      this.companyId = chip.dataset.companyId
    } else {
      // Toggle other chips normally
      chip.classList.toggle('active')

      const chipText = chip.querySelector('.chip__text').textContent
      switch(chipText) {
        case 'Comprehensive':
          this.searchByAll = chip.classList.contains('active')
          break
        case 'Exact':
          this.searchByExact = chip.classList.contains('active')
          break
        case 'Museum':
          this.onlyMuseum = chip.classList.contains('active')
          break
      }
    }

    // Trigger new search with updated preferences
    this.search()
  }

  search() {
    // Clear any existing timer
    clearTimeout(this.debounceTimer)

    // Show blank state if input is empty or below min chars
    if (!this.inputTarget.value || this.inputTarget.value.length < this.constructor.minSearchChars) {
      this.resultsTarget.style.display = 'none'
      this.blankTarget.style.display = 'flex'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = 'none'

      // If we're in single view mode and the input is empty, revert to all results
      if (this.currentViewMode === 'single' && !this.inputTarget.value) {
        this.currentViewMode = 'all'
        this.currentViewType = null
        this.backButtonTarget.style.display = 'none'
      }

      return
    }

    // Set a new timer
    this.debounceTimer = setTimeout(() => {
      this.executeSearch()
    }, this.debounceDelay)
  }

  executeSearch() {
    const query = this.inputTarget.value
    if (query.length < this.constructor.minSearchChars) {
      this.resultsTarget.style.display = 'none'
      this.blankTarget.style.display = 'flex'
      this.emptyTarget.style.display = 'none'
      this.numResultsTarget.style.display = 'none'
      // Reset all badge counts when query is cleared
      if (this.hasCompanyBadgeTarget) this.CompanyBadgeTarget.textContent = '0'
      if (this.hasAssetBadgeTarget) this.AssetBadgeTarget.textContent = '0'
      if (this.hasPasswordBadgeTarget) this.PasswordBadgeTarget.textContent = '0'
      if (this.hasArticleBadgeTarget) this.ArticleBadgeTarget.textContent = '0'
      if (this.hasWebsiteBadgeTarget) this.WebsiteBadgeTarget.textContent = '0'
      if (this.hasProcessBadgeTarget) this.ProcessBadgeTarget.textContent = '0'
      if (this.hasNumResultsTarget) this.numResultsTarget.textContent = '0 results'
      return
    }

    // If there's already a fetch in progress, abort it
    if (this.currentFetch) {
      this.currentFetch.abort()
    }

    // Show loading state before fetch
    this.showLoadingState()

    const currentType = this.getCurrentType()

    const params = new URLSearchParams({
      q: query,
      search_by_all: this.searchByAll,
      search_by_exact: this.searchByExact,
      include_archived: this.onlyMuseum
    })

    // Handle specific view mode first
    if (this.specificViewMode) {
      const typeToUse = this.getSingularType(this.specificViewType)
      params.append('type', typeToUse)
    }
    // Then handle regular view modes
    else if (this.currentViewMode === 'all') {
      params.append('consolidated_results', 'true')
    } else {
      // If in single view mode, set the type parameter
      const typeToUse = this.getSingularType(this.currentViewType)
      params.append('type', typeToUse)
    }

    // Add company parameter if company filter is active
    if (this.onlyCompany && this.companyId) {
      params.append('company', this.companyId)
    }

    const url = `/searches?${params.toString()}`

    // Check cache first
    const cacheKey = url
    if (this._searchCache && this._searchCache.has(cacheKey)) {
      const cachedData = this._searchCache.get(cacheKey)
      this.displayResults(cachedData, currentType)
      return
    }

    // Create an AbortController for this fetch
    const controller = new AbortController()
    this.currentFetch = controller

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "Cache-Control": "no-cache"
      },
      signal: controller.signal
    })
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok')
      return response.json()
    })
    .then(data => {
      // Only update the display if this is still the current fetch
      if (this.currentFetch === controller) {
        // Cache the results
        if (!this._searchCache) this._searchCache = new Map()
        this._searchCache.set(cacheKey, data)
        // Limit cache size to prevent memory issues
        if (this._searchCache.size > 50) {
          const firstKey = this._searchCache.keys().next().value
          this._searchCache.delete(firstKey)
        }

        this.displayResults(data, currentType)
      }
    })
    .catch(error => {
      // Only handle error if it's not an abort error
      if (error.name !== 'AbortError') {
        this.hideLoadingState()
      }
    })
    .finally(() => {
      // Clear the current fetch if it's this one
      if (this.currentFetch === controller) {
        this.currentFetch = null
      }
    })
  }

  showLoadingState() {
    // Show loading in results area - simplified without spinner
    this.resultsTarget.innerHTML = `
      <div class="searcher__loading">
        <p>Searching...</p>
      </div>
    `
    this.resultsTarget.style.display = 'block'
    this.blankTarget.style.display = 'none'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'block'

    // Show loading in badge counts - simplified without spinner
    if (this.hasCompanyBadgeTarget) {
      this.CompanyBadgeTarget.innerHTML = '...'
    }
    if (this.hasAssetBadgeTarget) {
      this.AssetBadgeTarget.innerHTML = '...'
    }
    if (this.hasPasswordBadgeTarget) {
      this.PasswordBadgeTarget.innerHTML = '...'
    }
    if (this.hasProcessBadgeTarget) {
      this.ProcessBadgeTarget.innerHTML = '...'
    }
    if (this.hasArticleBadgeTarget) {
      this.ArticleBadgeTarget.innerHTML = '...'
    }
    if (this.hasWebsiteBadgeTarget) {
      this.WebsiteBadgeTarget.innerHTML = '...'
    }
  }

  hideLoadingState() {
    // Reset badge counts to empty if needed
    if (this.hasCompanyBadgeTarget) {
      this.CompanyBadgeTarget.textContent = '0'
    }
    if (this.hasAssetBadgeTarget) {
      this.AssetBadgeTarget.textContent = '0'
    }
    if (this.hasPasswordBadgeTarget) {
      this.PasswordBadgeTarget.textContent = '0'
    }
    if (this.hasArticleBadgeTarget) {
      this.ArticleBadgeTarget.textContent = '0'
    }
    if (this.hasWebsiteBadgeTarget) {
      this.WebsiteBadgeTarget.textContent = '0'
    }
    if (this.hasProcessBadgeTarget) {
      this.ProcessBadgeTarget.textContent = '0'
    }

    // Show blank state
    this.resultsTarget.style.display = 'none'
    this.blankTarget.style.display = 'flex'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'none'
  }

  highlightMatch(text, query) {
    if (!query || query.length < this.constructor.minSearchChars) {
      return text
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')

    return text.replace(regex, '<strong class="searcher__highlight">$1</strong>')
  }

  // Calculate ranking score for a result based on highlighted text
  calculateRankingScore(result, query) {
    if (!query || query.length < this.constructor.minSearchChars) {
      return 0
    }

    let score = 0
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')
    
    // Check name field
    if (result.name) {
      const nameMatches = result.name.match(regex)
      if (nameMatches) {
        // Add points for having matches in name (higher priority)
        score += 100
        // Add points for each character matched in name
        score += nameMatches.reduce((sum, match) => sum + match.length, 0) * 10
      }
    }
    
    // Check company_name field
    if (result.company_name) {
      const companyMatches = result.company_name.match(regex)
      if (companyMatches) {
        // Add points for having matches in company name (lower priority than name)
        score += 50
        // Add points for each character matched in company name
        score += companyMatches.reduce((sum, match) => sum + match.length, 0) * 5
      }
    }
    
    return score
  }

  // Sort results by ranking score (highest first)
  sortResultsByRanking(results, query) {
    return results.sort((a, b) => {
      const scoreA = this.calculateRankingScore(a, query)
      const scoreB = this.calculateRankingScore(b, query)
      return scoreB - scoreA // Descending order
    })
  }

  displayResults(data, currentType) {
    // Set initial display states
    this.resultsTarget.style.display = 'block'
    this.blankTarget.style.display = 'none'
    this.emptyTarget.style.display = 'none'
    this.numResultsTarget.style.display = 'block'

    // Calculate total results
    let totalResults = 0
    if (Array.isArray(data)) {
      totalResults = data.length
    } else {
      totalResults = Object.values(data).reduce((sum, arr) => sum + (arr.results?.length || 0), 0)
    }
    this.numResultsTarget.textContent = `${totalResults} result${totalResults === 1 ? '' : 's'}`

    // Set initial selection to first result
    this.selectedIndex = 0

    const query = this.inputTarget.value
    const fragment = document.createDocumentFragment()

    // Define the order and display names for each type
    const types = [
      { key: 'companies', name: this.structureNameValue, icon: 'far fa-briefcase' },
      { key: 'assets', name: 'Assets', icon: 'far fa-circle' },
      { key: 'passwords', name: 'Passwords', icon: 'far fa-key' },
      { key: 'articles', name: 'Articles', icon: 'far fa-book-reader' },
      { key: 'processes', name: 'Processes', icon: 'far fa-check' },
      { key: 'websites', name: 'Websites', icon: 'far fa-globe' }
    ]

    // Filter out companies section if onlyCompany is true
    const filteredTypes = this.onlyCompany ? types.filter(type => type.key !== 'companies') : types

    // Handle specific view mode
    if (this.specificViewMode) {
      // Convert singular type to plural for display
      const pluralType = this.getPluralType(this.specificViewType)
      const type = filteredTypes.find(t => t.key === pluralType)

      if (type) {
        let typeResults = []
        if (Array.isArray(data)) {
          typeResults = data
        } else if (data[type.key]?.results) {
          typeResults = data[type.key].results
        } else if (data.results) {
          typeResults = data.results
        }

        if (typeResults.length > 0) {
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)
          
          const resultsDiv = document.createElement('div')
          resultsDiv.className = 'searcher__type-results'
          resultsDiv.innerHTML = sortedResults.map((result, index) => `
            <a href="${result.url}" class='searcher__result${index === 0 ? ' searcher__result--selected' : ''}' tabindex="-1" target="_top"
                data-action="mouseenter->search#mouseEnter mouseleave->search#mouseLeave">
              <main>
                ${result.object_type === 'Company' && result.logo ?
                  `<div class="searcher__result-logo-container">
                    <img src="${result.logo}" class="searcher__result-logo" alt="${result.name} logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"/>
                    <i class="searcher__result-icon fas fa-store-alt" style="display: none;"></i>
                   </div>` :
                  `<i class='searcher__result-icon ${result.icon || type.icon}' style="${result.object_type === 'Asset' && result.icon_color && result.icon_background_color ? `color: ${result.icon_color}; background-color: ${result.icon_background_color};` : ''}"></i>`
                }
                <div class="searcher__result_content">
                  <h2>${this.highlightMatch(result.name, query)}${result.archived ? '<span class="badge badge--archived">Archived</span>' : ''}</h2>
                  ${result.company_name ? `<p>${this.highlightMatch(result.company_name, query)}</p>` : ''}
                </div>
              </main>
              <span>${this.printType(result)}</span>
            </a>
          `).join('')

          this.resultsTarget.innerHTML = ''
          this.resultsTarget.appendChild(resultsDiv)
          this.resultsTarget.style.display = 'block'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'none'
          this.numResultsTarget.style.display = 'block'
        } else {
          this.resultsTarget.style.display = 'none'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'flex'
          this.numResultsTarget.style.display = 'none'
        }
      }
    }
    // Handle single view mode
    else if (this.currentViewMode === 'single') {
      const type = filteredTypes.find(t => t.key === this.currentViewType)

      if (type) {  // Only proceed if type exists
        // Handle both array and object data structures
        let typeResults = []
        if (Array.isArray(data)) {
          typeResults = data
        } else if (data[type.key]?.results) {
          typeResults = data[type.key].results
        } else if (data.results) {
          typeResults = data.results
        }

        if (typeResults.length > 0) {  // Only show section if there are results
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)
          
          const resultsDiv = document.createElement('div')
          resultsDiv.className = 'searcher__type-results'
          resultsDiv.innerHTML = sortedResults.map((result, index) => `
            <a href="${result.url}" class='searcher__result${index === 0 ? ' searcher__result--selected' : ''}' tabindex="-1" target="_top"
                data-action="mouseenter->search#mouseEnter mouseleave->search#mouseLeave">
              <main>
                ${result.object_type === 'Company' && result.logo ?
                  `<div class="searcher__result-logo-container">
                    <img src="${result.logo}" class="searcher__result-logo" alt="${result.name} logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"/>
                    <i class="searcher__result-icon fas fa-store-alt" style="display: none;"></i>
                   </div>` :
                  `<i class='searcher__result-icon ${result.icon || type.icon}' style="${result.object_type === 'Asset' && result.icon_color && result.icon_background_color ? `color: ${result.icon_color}; background-color: ${result.icon_background_color};` : ''}"></i>`
                }
                <div class="searcher__result_content">
                  <h2>${this.highlightMatch(result.name, query)}${result.archived ? '<span class="badge badge--archived">Archived</span>' : ''}</h2>
                  ${result.company_name ? `<p>${this.highlightMatch(result.company_name, query)}</p>` : ''}
                  ${result.object_type === 'Company' && result.nickname ? `<p>${this.highlightMatch(result.nickname, query)}</p>` : ''}
                </div>
              </main>
              <span>${this.printType(result)}</span>
            </a>
          `).join('')

          // Update the DOM for single view mode
          this.resultsTarget.innerHTML = ''
          this.resultsTarget.appendChild(resultsDiv)
          this.resultsTarget.style.display = 'block'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'none'
          this.numResultsTarget.style.display = 'block'
        } else {
          // Show empty state if no results for this type
          this.resultsTarget.style.display = 'none'
          this.blankTarget.style.display = 'none'
          this.emptyTarget.style.display = 'flex'
          this.numResultsTarget.style.display = 'none'
        }
      } else {
        // Show empty state if type not found
        this.resultsTarget.style.display = 'none'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'flex'
        this.numResultsTarget.style.display = 'none'
      }
    } else {
      let hasAnyResults = false
      let isFirstResult = true

      filteredTypes.forEach(type => {
        const typeResults = data[type.key]?.results || []

        if (typeResults.length > 0) {  // Only show section if there are results
          // Sort results by ranking score within this section
          const sortedResults = this.sortResultsByRanking(typeResults, query)
          
          hasAnyResults = true
          const section = document.createElement('div')
          section.className = 'searcher__type-section'
          section.innerHTML = `
            <div class="searcher__type-header">
              <i class="${type.icon}"></i>
              <h3>${type.name} (${typeResults.length})</h3>
            </div>
            <div class="searcher__type-results">
              ${sortedResults.slice(0, 5).map((result, index) => {
                const isSelected = isFirstResult && index === 0
                if (isSelected) isFirstResult = false
                return `
                <a href="${result.url}" class='searcher__result${isSelected ? ' searcher__result--selected' : ''}' tabindex="-1" target="_top"
                    data-action="mouseenter->search#mouseEnter mouseleave->search#mouseLeave">
                  <main>
                    ${result.object_type === 'Company' && result.logo ?
                      `<div class="searcher__result-logo-container">
                        <img src="${result.logo}" class="searcher__result-logo" alt="${result.name} logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"/>
                        <i class="searcher__result-icon fas fa-store-alt" style="display: none;"></i>
                       </div>` :
                      `<i class='searcher__result-icon ${result.icon || type.icon}' style="${result.object_type === 'Asset' && result.icon_color && result.icon_background_color ? `color: ${result.icon_color}; background-color: ${result.icon_background_color};` : ''}"></i>`
                    }
                    <div class="searcher__result_content">
                      <h2>${this.highlightMatch(result.name, query)}${result.archived ? '<span class="badge badge--archived">Archived</span>' : ''}</h2>
                      ${result.company_name ? `<p>${this.highlightMatch(result.company_name, query)}</p>` : ''}
                      ${result.object_type === 'Company' && result.nickname ? `<p>${this.highlightMatch(result.nickname, query)}</p>` : ''}
                    </div>
                  </main>
                  <span>${this.printType(result)}</span>
                </a>
              `}).join('')}
              ${typeResults.length > 5 ? `
                <div class="searcher__more-results">
                  <a href="#" class="searcher__more-link" data-action="click->search#viewAllResults" data-type="${type.key}" tabindex="0">
                    View all ${typeResults.length} ${type.key === 'companies' ? this.structurePluralValue.toLowerCase() : type.name.toLowerCase()}
                  </a>
                </div>
              ` : ''}
            </div>
          `
          fragment.appendChild(section)
        }
      })

      // If no sections have results, show the empty state
      if (!hasAnyResults) {
        this.resultsTarget.style.display = 'none'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'flex'
        this.numResultsTarget.style.display = 'none'
      } else {
        // Single DOM update
        this.resultsTarget.innerHTML = ''
        this.resultsTarget.appendChild(fragment)
        this.resultsTarget.style.display = 'block'
        this.blankTarget.style.display = 'none'
        this.emptyTarget.style.display = 'none'
      }
    }

    // Refocus the input and rebind keyboard events
    this.inputTarget.focus()
    this.bindKeyboardEvents()
  }

  printType(result) {
    switch(result.object_type) {
      case 'Asset':
        return result.asset_type
      default:
        return ""
    }
  }

  mouseEnter(event) {
    // Don't add selected class on hover, just let CSS :hover handle it
    const results = Array.from(this.resultsTarget.querySelectorAll('.searcher__result'))
    this.selectedIndex = results.indexOf(event.currentTarget)
  }

  mouseLeave(event) {
    // Don't remove selected class on mouse leave, only on keyboard navigation
    this.selectedIndex = -1
  }

  close() {
    const frame = this.element.closest('turbo-frame')
    if (frame) frame.src = null
  }
  // Add helper methods for getting icons and URLs
  getIconForType(type) {
    switch(type) {
      case 'Asset':
        return 'fas fa-cube'
      case 'Company':
        return 'fas fa-building'
      case 'Password':
      case 'AssetPassword':
        return 'fas fa-key'
      case 'Article':
        return 'fas fa-file-alt'
      case 'Website':
        return 'fas fa-globe'
      case 'Process':
        return 'fas fa-check'
      default:
        return 'fas fa-question'
    }
  }

  getUrlForType(id, type) {
    switch(type) {
      case 'Asset':
        return `/assets/${id}`
      case 'Company':
        return `/companies/${id}`
      case 'Password':
      case 'AssetPassword':
        return `/asset_passwords/${id}`
      case 'Article':
        return `/articles/${id}`
      case 'Website':
        return `/websites/${id}`
      case 'Process':
        return `/processes/${id}`
      default:
        return '#'
    }
  }

  disconnect() {
    // Abort any pending fetch when disconnecting
    if (this.currentFetch) {
      this.currentFetch.abort()
    }

    // Clear any pending debounce timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    // Remove event listeners
    this.element.removeEventListener('keydown', this.handleKeydown)

    // Show header search when modal closes
    const headerSearch = document.querySelector('.header__search')
    if (headerSearch) {
      headerSearch.classList.remove('search--hidden')
    }
  }

  // Helper to determine the type from icon class
  getTypeFromIconClass(className) {
    if (className.includes('fa-briefcase')) return 'companies'
    if (className.includes('fa-circle')) return 'assets'
    if (className.includes('fa-key')) return 'passwords'
    if (className.includes('fa-book-reader')) return 'articles'
    if (className.includes('fa-globe')) return 'websites'
    if (className.includes('fa-check')) return 'processes'
    return 'assets' // Default to assets if unknown
  }

  // Convert plural type to singular for API
  getSingularType(pluralType) {
    const typeMap = {
      'companies': 'Company',
      'assets': 'Asset',
      'passwords': 'Password',
      'articles': 'Article',
      'websites': 'Website',
      'processes': 'Process'
    }
    return typeMap[pluralType] || 'Asset'
  }

  // Convert singular type to plural for display
  getPluralType(singularType) {
    const typeMap = {
      'Company': 'companies',
      'Asset': 'assets',
      'Password': 'passwords',
      'Article': 'articles',
      'Website': 'websites',
      'Process': 'processes'
    }
    return typeMap[singularType] || 'assets'
  }

  showInitialEmptyState() {
    const types = [
      { key: 'companies', name: this.structureNameValue, icon: 'far fa-briefcase' },
      { key: 'assets', name: 'Assets', icon: 'far fa-circle' },
      { key: 'passwords', name: 'Passwords', icon: 'far fa-key' },
      { key: 'articles', name: 'Articles', icon: 'far fa-book-reader' },
      { key: 'processes', name: 'Processes', icon: 'far fa-check' },
      { key: 'websites', name: 'Websites', icon: 'far fa-globe' }
    ]

    let html = types.map(type => `
      <div class="searcher__type-section">
        <div class="searcher__type-header">
          <i class="${type.icon}"></i>
          <h3>${type.name} (0)</h3>
        </div>
        <div class="searcher__type-results">
        </div>
      </div>
    `).join('')

    this.resultsTarget.innerHTML = html
    this.resultsTarget.style.display = 'block'
  }

  viewAllResults(event) {
    event.preventDefault()
    const type = event.currentTarget.dataset.type
    this.currentViewMode = 'single'
    this.currentViewType = type
    this.backButtonTarget.style.display = 'flex'
    this.search()
  }

  backToAllResults() {
    this.currentViewMode = 'all'
    this.currentViewType = null
    this.backButtonTarget.style.display = 'none'
    this.search()
  }
}
